/* eslint-disable max-len */
/**
 * @fileoverview Updated Visual Studio: The Command Center for AI Media.
 *               PATH: src/components/contentHub/VisualGenerator.js
 *               UPDATED: Dark Theme Integration
 */

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
    FiCheckCircle, FiTrash2, FiUploadCloud, FiLoader, FiFacebook,
    FiInstagram, FiLayout, FiArrowLeft, FiChevronDown, FiChevronRight,
    FiEdit2, FiLayers
} from 'react-icons/fi';
import { FaLinkedin } from 'react-icons/fa';
import VisualGenerationPanel from './VisualGenerationPanel';
import VideoGenerationPanel from './VideoGenerationPanel';
import VisualBadge from '../common/VisualBadge';
import { storage, functions } from '../../firebaseConfig';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { useToast } from '../shared/contexts/ToastContext';
import { useConfirmDialog } from '../shared/contexts/ConfirmContext';
import { isVideoTemplate } from '../shared/utils/mediaTypeUtils';
import TextOverlayControls from './TextOverlayControls';

const processVisualFn = httpsCallable(functions, 'processVisual');
const generateImagePromptFn = httpsCallable(functions, 'generateImagePrompt'); // Architect
const generateImageFromPromptFn = httpsCallable(functions, 'generateImageFromPrompt'); // Renderer
const generateVideoFromPromptFn = httpsCallable(functions, 'generateVideoFromPrompt'); // Video Renderer

const PlatformIcon = ({ platform }) => {
    if (platform === 'facebook') return <FiFacebook className="text-blue-400"/>;
    if (platform === 'instagram') return <FiInstagram className="text-pink-400"/>;
    if (platform === 'linkedin') return <FaLinkedin className="text-[#0077B5]"/>;
    return <FiLayout />;
};

const PostTextPreview = ({ text }) => {
    const [isOpen, setIsOpen] = useState(false);
    if (!text) return null;
    return (
        <div className="border-t border-white/5 bg-warm-gray/20">
            <button 
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between px-4 py-3 text-[10px] font-bold text-white/40 hover:text-slate-blue transition-colors uppercase tracking-widest"
            >
                <span>Caption Context</span>
                {isOpen ? <FiChevronDown/> : <FiChevronRight/>}
            </button>
            {isOpen && (
                <div className="px-4 pb-4 animate-fade-in text-left">
                    <div className="text-xs text-white/80 whitespace-pre-wrap bg-warm-dark border border-white/5 rounded-lg p-3 shadow-inner max-h-32 overflow-y-auto custom-scrollbar">
                        {text}
                    </div>
                </div>
            )}
        </div>
    );
};

function VisualGenerator({
    project,
    posts,
    workflowType,
    onSetPostVisual,
    onSetPostPrompt,
    onVisualsConfirmed,
    onSettingsChange,
    loading,
    adjustments,
    onBack,
}) {
    const { addToast } = useToast();
    const { confirm } = useConfirmDialog();

    // --- State ---
    const [textPreviewElements, setTextPreviewElements] = useState({}); // Per-platform text preview {facebook: [...], instagram: [...]}
    const [activePostIndex, setActivePostIndex] = useState({ facebook: 0, instagram: 0, linkedin: 0 }); // Per-platform navigation
    const [activeCarouselSlide, setActiveCarouselSlide] = useState({ facebook: 0, instagram: 0, linkedin: 0 }); // Track per-platform
    // NEW: Master Session Source of Truth (Split by Platform)
    const [masterSessionSettings, setMasterSessionSettings] = useState(() => {
        // Initialize with Project Defaults for both keys to start
        const defaults = project.defaultVisualSettings || { 
            categoryId: "professional", vibeId: "smart_auto", textStrategy: "none"
        };
        return {
            facebook: { ...defaults },
            instagram: { ...defaults },
            linkedin: { ...defaults }
        };
    });
    const [isBatchGenerating, setIsBatchGenerating] = useState(false);
    const [batchProgress, setBatchProgress] = useState('');
    const [uploadingState, setUploadingState] = useState({ index: null, platform: null }); 
    const [editModeState, setEditModeState] = useState({});

    // useRef to track latest posts without triggering re-renders
    // Prevents stale closure reads in Firestore listener
    const postsRef = useRef(posts);
    useEffect(() => {
        postsRef.current = posts;
    }, [posts]);

    // --- Logic 1: Platform Filtering (The "Meta Leak" Fix) ---
    const platformsToRender = useMemo(() => {
        // Start with what the user requested in Step 2
        let requested = adjustments?.platforms || ['facebook'];

        // Filter by ACTUAL connection status
        // If the project says "Meta" but only has FB linked, we must hide IG.
        return requested.filter(p => {
            if (p === 'facebook') return !!project.associatedPageId;
            if (p === 'instagram') return !!project.associatedInstagramId;
            if (p === 'linkedin') return !!project.associatedLinkedInId;
            return true; // Fallback for generic
        });
    }, [adjustments, project]);

    // Merge: first platform reference for merged column
    // (kept for backward compat — used in rendering logic below)

    // --- Helpers ---
    const getVisualForPlatform = (post, platform, platformAdjustments) => {
        // 1. Check Carousel Readiness
        const tmpl = platformAdjustments?.templates?.[platform] || "standard";
        if (tmpl.includes('carousel')) {
            const sequence = post.visualSequence?.[platform] || [];
            // A carousel is "Visual Ready" only if it has slides and ALL have URLs
            if (sequence.length > 0 && sequence.every(s => s.visual?.url)) {
                return {
                    url: sequence[0].visual.url,
                    type: 'carousel_generated',
                    metadata: {
                        ...(sequence[0].visual.metadata || {}),
                        isCarouselWrapper: true,
                        slideCount: sequence.length
                    }
                };
            }
            return null; // Not fully ready
        }

        // 2. Standard Post Check
        if (!post.visual) return null;
        if (post.visual[platform]) return post.visual[platform];
        if (post.visual.url && !post.visual.facebook && !post.visual.instagram && !post.visual.linkedin) return post.visual;
        return null;
    };

    const toggleEditMode = (key) => {
        setEditModeState(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // --- Handlers ---
    
    // Bridge: Captures the style from the panel and updates the Master Session
    const handleSettingsUpdate = useCallback((newSettings, platform) => {
        setMasterSessionSettings(prev => ({
            ...prev,
            [platform]: newSettings
        }));
        if (onSettingsChange) onSettingsChange(newSettings); // Bubble up if needed
    }, [onSettingsChange]);

    const handleUpload = async (file, index, platform) => {
        // File size validation (Phase 4: T4.4)
        const isVideoFile = file.type.startsWith('video/');
        const maxSize = isVideoFile ? 100 * 1024 * 1024 : 10 * 1024 * 1024; // 100MB video, 10MB image
        if (file.size > maxSize) {
            addToast(`File too large. Max ${isVideoFile ? '100MB' : '10MB'}.`, "error");
            return;
        }

        const post = posts[index];
        setUploadingState({ index, platform });
        const ext = file.name.split('.').pop()?.toLowerCase() || (isVideoFile ? 'mp4' : 'jpg');
        const storagePath = `projectVisuals/${project.userId}/${project.id}/posts/${post.id}/${platform}_visual.${ext}`;
        const storageRef = ref(storage, storagePath);
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on('state_changed', null,
            () => { addToast("Upload failed", "error"); setUploadingState({ index: null, platform: null }); },
            async () => {
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                const visualData = { type: isVideoFile ? 'video' : 'upload', url: downloadURL, metadata: { storagePath, name: file.name, size: file.size, type: file.type, contentType: file.type } };
                
                // FIX: Manual Upload Blindness
                // We must use the status returned by the server, which runs the complex gatekeeper logic.
                const result = await processVisualFn({ projectId: project.id, postId: post.id, visualData, platform });
                
                // Pass the new status to the parent so the UI flips green immediately
                onSetPostVisual(index, visualData, platform, result.data.newStatus); 
                
                setEditModeState(prev => ({ ...prev, [`${index}-${platform}`]: false }));
                setUploadingState({ index: null, platform: null });
                addToast(`${platform} asset uploaded!`, "success");
            }
        );
    };

    const handleRemoveVisual = useCallback(async (index, platform) => {
        const post = posts[index];
        const visual = getVisualForPlatform(post, platform, adjustments);
        if (!visual) return;
        const confirmed = await confirm({
            title: 'Delete Visual',
            message: 'Delete this visual?',
            confirmLabel: 'Delete',
            variant: 'danger'
        });
        if (!confirmed) return;
        try {
            if (visual.type === 'upload' && visual.metadata?.storagePath) {
                 await deleteObject(ref(storage, visual.metadata.storagePath)).catch(() => {});
            }

            // Merge: if deleting from a merged post, clear ALL merged platforms (they share the same file)
            if (canMerge) {
                for (const mp of platformsToRender) {
                    await processVisualFn({ projectId: project.id, postId: post.id, visualData: null, platform: mp });
                    onSetPostVisual(index, null, mp);
                    setEditModeState(prev => ({ ...prev, [`${index}-${mp}`]: true }));
                }
            } else {
                await processVisualFn({ projectId: project.id, postId: post.id, visualData: null, platform });
                onSetPostVisual(index, null, platform);
                setEditModeState(prev => ({ ...prev, [`${index}-${platform}`]: true }));
            }
            addToast("Visual removed.", "info");
        } catch (err) { addToast('Failed to remove visual.', 'error'); }
    }, [posts, project, adjustments, platformsToRender, onSetPostVisual, addToast, confirm]);

    const handleVisualUpdateFromAI = useCallback((postId, newVisual, platform, newStatus, newSequence) => {
        const postIndex = posts.findIndex(p => p.id === postId);
        if (postIndex === -1) return;

        // Merge: update ALL non-carousel platforms when visual is generated
        const isMergeable = canMerge && newVisual;
        if (isMergeable) {
            for (const mp of platformsToRender) {
                onSetPostVisual(postIndex, newVisual, mp, newStatus, newSequence);
                setEditModeState(prev => ({ ...prev, [`${postIndex}-${mp}`]: false }));
            }
        } else {
            onSetPostVisual(postIndex, newVisual, platform, newStatus, newSequence);
            setEditModeState(prev => ({ ...prev, [`${postIndex}-${platform}`]: false }));
        }
    }, [posts, platformsToRender, adjustments, onSetPostVisual]);



    // --- Logic 2B: Platform-Specific Batch Generation ---
    const handleGenerateAllForPlatform = useCallback(async (targetPlatform) => {
        const postsNeedingVisuals = posts.filter(post => {
            return !getVisualForPlatform(post, targetPlatform, adjustments);
        });

        if (postsNeedingVisuals.length === 0) {
            addToast(`All ${targetPlatform} posts already have visuals.`, "info");
            return;
        }

        const confirmed = await confirm({
            title: 'Batch Generate',
            message: `Auto-generate ${targetPlatform} visuals for ${postsNeedingVisuals.length} posts?`,
            confirmLabel: 'Generate',
            variant: 'default'
        });
        if (!confirmed) return;

        setIsBatchGenerating(true);
        let successCount = 0;

        for (const post of postsNeedingVisuals) {
            try {
                // SKIP CAROUSELS (Legacy Batch doesn't support 5-slide iteration yet)
                const postAdjustments = post.adjustments || adjustments || {};
                const isCarousel = postAdjustments.templates?.[targetPlatform]?.includes('carousel');
                
                if (isCarousel) {
                    continue;
                }

                const effectiveSettings = masterSessionSettings[targetPlatform] || project.defaultVisualSettings || {
                    categoryId: "photorealistic", vibeId: "general", textStrategy: "none"
                };

                // Check if this is a video template (Phase 4: T4.3)
                const isVideo = isVideoTemplate(postAdjustments.templates?.[targetPlatform]);

                if (isVideo) {
                    // Video generation - full pipeline handled by Cloud Function
                    const duration = 5;
                    setBatchProgress('Generating 5s video clip... (~30s)');

                    const res = await generateVideoFromPromptFn({
                        projectId: project.id,
                        postId: post.id,
                        platform: targetPlatform,
                        videoDuration: duration,
                        settings: effectiveSettings
                    });
                    if (res.data?.success) {
                        handleVisualUpdateFromAI(post.id, res.data.visual, targetPlatform, res.data.newStatus);
                        successCount++;
                    }
                } else {
                    // Image generation - existing 2-step flow
                    setBatchProgress('Generating image...');

                    // Step A: Ensure Blueprint Exists
                    let currentBlueprint = post.visualPrompt;

                    if (!currentBlueprint) {
                        const architectSettings = masterSessionSettings[targetPlatform] || project.defaultVisualSettings;

                        const architectRes = await generateImagePromptFn({
                            projectId: project.id,
                            postId: post.id,
                            visualSettings: architectSettings
                        });
                        if (architectRes.data?.success) {
                            currentBlueprint = architectRes.data.prompt;
                            onSetPostPrompt(posts.findIndex(p => p.id === post.id), currentBlueprint);
                        } else {
                            throw new Error("Architect failed in batch.");
                        }
                    }

                    // Step B: Render for this platform only
                    const res = await generateImageFromPromptFn({
                        projectId: project.id,
                        postId: post.id,
                        prompt: currentBlueprint,
                        platform: targetPlatform,
                        settings: effectiveSettings
                    });

                    if (res.data?.success) {
                        handleVisualUpdateFromAI(post.id, res.data.visual, targetPlatform, res.data.newStatus);
                        successCount++;
                    }
                }
            } catch (err) {
                addToast(`Failed to generate ${targetPlatform} visual for post ${post.id.substring(0,4)}`, 'error');
            }
        }
        setIsBatchGenerating(false);
        setBatchProgress('');
        addToast(`${targetPlatform} batch complete! Generated ${successCount} new visuals.`, "success");
    }, [posts, adjustments, project, masterSessionSettings, onSetPostPrompt, addToast, handleVisualUpdateFromAI, confirm]);

    if (loading && !posts.length) return <div className="text-center p-20"><FiLoader className="mx-auto animate-spin h-10 w-10 text-slate-blue"/></div>;

    // Merge: when ALL platforms have non-carousel formats, render one merged column
    const canMerge = platformsToRender.length > 1 && platformsToRender.every(p => {
        const tmpl = adjustments?.templates?.[p] || 'standard';
        return !tmpl.includes('carousel');
    });
    const firstMergedPlatform = canMerge ? platformsToRender[0] : null;

    // currentPost is now calculated per-platform inside the loop
    const gridCols = (!canMerge && platformsToRender.length > 1) ? 'lg:grid-cols-2' : 'grid-cols-1';

    return (
        <div className="space-y-4 p-4 md:p-6 card-base">
            
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 border-b border-white/5 pb-4">
                <div className="flex items-center">
                    <button onClick={onBack} aria-label="Go Back" className="mr-4 p-2 text-white/50 hover:text-white rounded-full hover:bg-white/5 transition-all">
                        <FiArrowLeft size={20} />
                    </button>
                    <div>
                        <h2 className="text-xl font-bold text-white tracking-tight text-left uppercase">Visual Studio</h2>
                        <p className="text-xs text-slate-blue font-bold text-left tracking-widest uppercase">Create stunning visuals for your posts</p>
                    </div>
                </div>

                {/* Post count indicator */}
                <div className="flex items-center gap-4">
                    {isBatchGenerating && batchProgress && (
                        <div className="px-4 py-2 bg-purple-500/10 rounded-xl text-xs font-bold text-purple-300 border border-purple-500/30 animate-pulse">
                            <FiLoader className="inline animate-spin mr-2" />{batchProgress}
                        </div>
                    )}
                    {posts.length > 1 && (
                        <div className="px-4 py-2 bg-warm-gray/30 rounded-xl text-xs font-bold text-white/70 uppercase tracking-wide border border-white/5">
                            {posts.length} Posts • Use pills below to navigate
                        </div>
                    )}
                </div>
            </div>

            {/* Main Workspace */}
            <div className="animate-fade-in space-y-4">

                <div className={`grid gap-8 ${gridCols} w-full`}>
                    {(canMerge ? [firstMergedPlatform] : platformsToRender).map(platform => {
                        const isMerged = canMerge && platform === firstMergedPlatform;
                        const platformPostIndex = activePostIndex[platform] || 0;
                        const currentPost = posts[platformPostIndex];
                        const visual = getVisualForPlatform(currentPost, platform, adjustments);
                        const hasVisual = !!visual;
                        const editKey = `${platformPostIndex}-${platform}`;
                        const showStudio = !hasVisual || editModeState[editKey];
                        const platformText = typeof currentPost.content === 'object' ? currentPost.content[platform] : currentPost.content;
                        const columnLabel = isMerged
                            ? platformsToRender.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' & ')
                            : platform.charAt(0).toUpperCase() + platform.slice(1) + ' Variant';

                        return (
                            <div key={isMerged ? 'video-merged' : platform} className="bg-warm-gray/20 rounded-3xl border border-white/5 shadow-card overflow-hidden flex flex-col transition-all">
                                <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between bg-warm-gray/30">
                                    <div className="flex items-center gap-2 font-bold text-white/90">
                                        {isMerged
                                            ? platformsToRender.map(p => <PlatformIcon key={p} platform={p} />)
                                            : <PlatformIcon platform={platform} />
                                        }
                                        <span>{columnLabel}</span>
                                    </div>
                                    {hasVisual && <FiCheckCircle className="text-green-400" size={20}/>}
                                </div>

                                <div className={`flex flex-col justify-center bg-warm-gray/20 items-center gap-4 ${hasVisual ? 'p-8 min-h-[350px]' : 'p-4'}`}>
                                    {/* Main Media Container */}
                                    {(() => {
                                        // USE ADJUSTMENTS PROP, NOT currentPost.adjustments!
                                        const tmpl = adjustments?.templates?.[platform] || "standard";
                                        const isCarousel = tmpl.includes("carousel");
                                        const sequence = currentPost.visualSequence?.[platform] || [];

                                        // 1. CAROUSEL MODE (Partial or Ready)
                                        if (isCarousel && sequence.length > 0) {
                                            const platformSlideIndex = activeCarouselSlide[platform] || 0;
                                            const activeSlide = sequence[platformSlideIndex] || sequence[0];
                                            const displayUrl = activeSlide?.visual?.url;
                                            
                                            if (displayUrl) {
                                                // FIX: Don't append cache-buster to signed URLs (breaks signature)
                                                const isSignedUrl = displayUrl.includes('X-Goog-Signature');
                                                const carouselDisplayUrl = isSignedUrl 
                                                    ? displayUrl 
                                                    : `${displayUrl}${displayUrl.includes('?') ? '&' : '?'}_t=${Date.now()}`;
                                                
                                                // FIX: Force React to remount img on regeneration
                                                const signatureMatch = displayUrl.match(/X-Goog-Signature=([a-f0-9]+)/);
                                                const carouselUniqueKey = signatureMatch ? signatureMatch[1].slice(-16) : displayUrl;
                                                
                                                return (
                                                    <div className="relative group w-full max-w-sm aspect-[4/5] bg-warm-dark rounded-2xl overflow-hidden shadow-2xl border border-white/5 flex items-center justify-center">
                                                        <img 
                                                            key={carouselUniqueKey} 
                                                            src={carouselDisplayUrl} 
                                                            alt={`Slide ${platformSlideIndex + 1}`} 
                                                            className="w-full h-full object-contain" 
                                                        />
                                                        
                                                        {/* Carousel Badge */}
                                                        <div className="absolute top-4 left-4 bg-slate-blue text-warm-dark px-3 py-1 rounded-full text-[10px] font-black shadow-lg flex items-center gap-2">
                                                            <FiLayers /> CAROUSEL
                                                        </div>

                                                        {/* Hover Controls */}
                                                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-6">
                                                            <button onClick={() => toggleEditMode(editKey)} aria-label="Edit Slide" className="p-4 bg-white text-warm-dark rounded-full shadow-2xl transform hover:scale-110 transition-all"><FiEdit2 size={24} /></button>
                                                            <button onClick={() => handleRemoveVisual(platformPostIndex, platform)} aria-label="Delete Slide" className="p-4 bg-red-500 text-white rounded-full shadow-2xl transform hover:scale-110 transition-all"><FiTrash2 size={24} /></button>
                                                        </div>
                                                        {/* Page Badge if ready */}
                                                        {visual?.metadata && <div className="absolute bottom-4 left-4"><VisualBadge metadata={visual.metadata} /></div>}
                                                    </div>
                                                );
                                            } else {
                                                return (
                                                    <div className="relative group w-full max-w-sm aspect-[4/5] bg-warm-dark rounded-2xl overflow-hidden shadow-2xl border border-white/5 flex items-center justify-center">
                                                        <div className="text-center p-6">
                                                            <FiLayers size={48} className="mx-auto text-slate-blue mb-4 opacity-50" />
                                                            <p className="text-xs font-bold text-white/60 uppercase tracking-widest">Carousel Sequence</p>
                                                            <p className="text-[10px] text-white/40 mt-2">{sequence.length} Slides Pending</p>
                                                        </div>
                                                        <div className="absolute top-4 left-4 bg-slate-blue text-warm-dark px-3 py-1 rounded-full text-[10px] font-black shadow-lg flex items-center gap-2">
                                                            <FiLayers /> CAROUSEL
                                                        </div>
                                                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-6">
                                                            <button onClick={() => toggleEditMode(editKey)} aria-label="Edit Slide" className="p-4 bg-white text-warm-dark rounded-full shadow-2xl transform hover:scale-110 transition-all"><FiEdit2 size={24} /></button>
                                                        </div>
                                                    </div>
                                                );
                                            }
                                        }

                                        // 2. STANDARD MODE (Ready)
                                        if (hasVisual) {
                                            // Don't append cache-buster to signed URLs (breaks signature)
                                            const isSignedUrl = visual.url.includes('X-Goog-Signature');
                                            const displayUrl = isSignedUrl
                                                ? visual.url
                                                : `${visual.url}${visual.url.includes('?') ? '&' : '?'}_t=${Date.now()}`;

                                            // Force React to remount on regeneration using signature as unique key
                                            const signatureMatch = visual.url.match(/X-Goog-Signature=([a-f0-9]+)/);
                                            const uniqueKey = signatureMatch ? signatureMatch[1].slice(-16) : visual.url;
                                            const isVideoVisual = visual.type === 'video' || visual.metadata?.contentType === 'video/mp4';

                                            return (
                                                <div className={`relative group w-full max-w-sm ${isVideoVisual ? 'aspect-[9/16]' : 'aspect-[4/5]'} bg-warm-dark rounded-2xl overflow-hidden shadow-2xl border border-white/5 flex items-center justify-center`}>
                                                    {isVideoVisual ? (
                                                        <video
                                                            key={uniqueKey}
                                                            controls
                                                            loop
                                                            muted
                                                            autoPlay
                                                            playsInline
                                                            className="w-full h-full object-contain"
                                                        >
                                                            <source src={displayUrl} type={visual.metadata?.contentType || 'video/mp4'} />
                                                        </video>
                                                    ) : (
                                                        <img
                                                            key={uniqueKey}
                                                            src={displayUrl}
                                                            alt="Design"
                                                            className="w-full h-full object-contain"
                                                        />
                                                    )}

                                                    {/* Hover Controls */}
                                                    <div className="absolute top-3 right-3 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity flex items-center gap-2 z-10">
                                                        <button onClick={() => toggleEditMode(editKey)} aria-label="Edit Visual" className="p-2 bg-white text-warm-dark rounded-full shadow-2xl transform hover:scale-110 transition-all"><FiEdit2 size={16} /></button>
                                                        <button onClick={() => handleRemoveVisual(platformPostIndex, platform)} aria-label="Delete Visual" className="p-2 bg-red-500 text-white rounded-full shadow-2xl transform hover:scale-110 transition-all"><FiTrash2 size={16} /></button>
                                                    </div>
                                                    {visual?.metadata && <div className="absolute bottom-4 left-4"><VisualBadge metadata={visual.metadata} /></div>}

                                                    {/* Live CSS Preview — text overlay mock on video (proportional to 720px frame) */}
                                                    {isVideoVisual && (textPreviewElements[platform] || []).length > 0 && (
                                                        <div className="absolute inset-0 pointer-events-none z-[5]">
                                                            {(textPreviewElements[platform] || []).filter(el => el.text?.trim()).map((el, i) => {
                                                                // Scale: preview container is ~384px (max-w-sm), video is 720px. Ratio ≈ 0.53
                                                                const fontSizePx = el.fontSize === 'small' ? 19 : el.fontSize === 'medium' ? 25 : 32;
                                                                const fontFamily = el.fontFamily === 'oswald' ? "'Oswald', sans-serif" : el.fontFamily === 'playfair' ? "'Playfair Display', serif" : "'Inter', sans-serif";
                                                                const fontWeight = el.fontFamily === 'playfair' ? 700 : el.fontFamily === 'oswald' ? 600 : 700;
                                                                const outlineColor = el.outlineColor || '#000';
                                                                const posStyle = el.position === 'top'
                                                                    ? {top: '8%'}
                                                                    : el.position === 'bottom'
                                                                        ? {bottom: '12%'}
                                                                        : {top: '50%', transform: 'translateY(-50%)'};
                                                                return (
                                                                    <div key={i} className="absolute left-0 right-0 flex justify-center px-3" style={posStyle}>
                                                                        <div className="text-center max-w-[92%]" style={{
                                                                            backgroundColor: el.showBackground ? 'rgba(0,0,0,0.5)' : 'transparent',
                                                                            padding: el.showBackground ? '8px 16px' : '0',
                                                                            borderRadius: el.showBackground ? '4px' : '0',
                                                                        }}>
                                                                            <span
                                                                                className="leading-tight break-words"
                                                                                style={{
                                                                                    fontSize: `${fontSizePx}px`,
                                                                                    fontFamily,
                                                                                    fontWeight,
                                                                                    color: el.fontColor || '#FFFFFF',
                                                                                    WebkitTextStroke: `2px ${outlineColor}`,
                                                                                    paintOrder: 'stroke fill',
                                                                                    letterSpacing: el.fontFamily === 'oswald' ? '1px' : '0',
                                                                                }}
                                                                            >
                                                                                {el.text}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        }

                                        // 3. UPLOAD PLACEHOLDER
                                        const isVideoTmpl = isVideoTemplate(tmpl);
                                        const acceptTypes = isVideoTmpl ? "video/mp4,video/quicktime" : "image/*";
                                        return (
                                            <div className="w-full flex justify-center">
                                                <input type="file" accept={acceptTypes} className="hidden" id={`upload-${platformPostIndex}-${platform}`} onChange={(e) => {
                                                    const f = e.target.files?.[0]; if (f) handleUpload(f, platformPostIndex, platform);
                                                }} />
                                                <label htmlFor={`upload-${platformPostIndex}-${platform}`} className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 border border-white/10 rounded-xl hover:border-slate-blue hover:bg-white/5 transition-all group">
                                                    {uploadingState.index === platformPostIndex && uploadingState.platform === platform
                                                        ? <FiLoader className="animate-spin text-slate-blue" size={14} />
                                                        : <FiUploadCloud className="text-white/20 group-hover:text-slate-blue" size={14} />}
                                                    <span className="text-[10px] font-bold text-white/40 group-hover:text-slate-blue uppercase tracking-widest">
                                                        {isVideoTmpl ? 'Upload Video' : 'Upload File'}
                                                    </span>
                                                </label>
                                            </div>
                                        );
                                    })()}

                                    {/* Carousel Filmstrip Navigation - Positioned under image */}
                                    {(() => {
                                        const tmpl = adjustments?.templates?.[platform] || "standard";
                                        const isCarousel = tmpl.includes("carousel");
                                        const sequence = currentPost.visualSequence?.[platform] || [];
                                        
                                        if (isCarousel && sequence.length > 0) {
                                            const platformSlideIndex = activeCarouselSlide[platform] || 0;
                                            
                                            return (
                                                <div className="w-full max-w-sm mt-4">
                                                    <div className="flex justify-between items-center mb-2">
                                                        <h4 className="text-[10px] font-black text-slate-blue uppercase tracking-widest flex items-center gap-2">
                                                            <FiLayers /> Carousel Studio
                                                        </h4>
                                                        <span className="text-[9px] text-white/40 font-bold uppercase">{sequence.length} Slides</span>
                                                    </div>
                                                    <div className="flex gap-2 overflow-x-auto pb-1">
                                                        {sequence.map((slide, idx) => {
                                                            const isActive = idx === platformSlideIndex;
                                                            const hasVisual = slide.visual?.url;
                                                            const hasError = slide.status === 'error';

                                                            // Status Pills - thin horizontal bars
                                                            let pillColor = 'bg-dark-border'; // pending
                                                            if (hasError) pillColor = 'bg-red-500'; // error
                                                            else if (hasVisual) pillColor = 'bg-green-500'; // ready
                                                            if (isActive) pillColor = 'bg-slate-blue shadow-glow'; // active override

                                                            return (
                                                                <button
                                                                    key={idx}
                                                                    onClick={() => setActiveCarouselSlide(prev => ({ ...prev, [platform]: idx }))}
                                                                    className={`
                                                                        relative flex-1 rounded-full transition-all
                                                                        ${pillColor}
                                                                        ${isActive ? 'h-3' : 'h-2 opacity-70 hover:opacity-100'}
                                                                    `}
                                                                    title={`Slide ${idx + 1} - ${hasVisual ? 'Ready' : hasError ? 'Error' : 'Pending'}`}
                                                                >
                                                                    <span className="sr-only">Slide {idx + 1}</span>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        }
                                        return null;
                                    })()}

                                    {/* Post Navigation Pills - Per-Platform (Stream Mode Only) */}
                                    {workflowType === 'stream' && posts.length > 1 && (
                                        <div className="w-full max-w-sm mt-4 border-t border-white/5 pt-4">
                                            <div className="flex justify-between items-center mb-2">
                                                <h4 className="text-[10px] font-black text-white/40 uppercase tracking-widest flex items-center gap-2">
                                                    <FiLayout /> Post Navigation
                                                </h4>
                                                <span className="text-[9px] text-white/30 font-bold uppercase">{posts.length} Posts</span>
                                            </div>
                                            <div className="flex gap-2 overflow-x-auto pb-1">
                                                {posts.map((post, idx) => {
                                                    const isActive = idx === platformPostIndex;
                                                    const hasVisual = !!getVisualForPlatform(post, platform, adjustments);

                                                    // Status Pills - thin horizontal bars
                                                    let pillColor = 'bg-dark-border'; // pending
                                                    if (hasVisual) pillColor = 'bg-green-500'; // ready
                                                    if (isActive) pillColor = 'bg-slate-blue shadow-glow'; // active override

                                                    return (
                                                        <button
                                                            key={post.id}
                                                            onClick={() => setActivePostIndex(prev => ({ ...prev, [platform]: idx }))}
                                                            className={`
                                                                relative flex-1 rounded-full transition-all
                                                                ${pillColor}
                                                                ${isActive ? 'h-3' : 'h-2 opacity-70 hover:opacity-100'}
                                                            `}
                                                            title={`Post ${idx + 1} - ${hasVisual ? 'Ready' : 'Pending'}`}
                                                        >
                                                            <span className="sr-only">Post {idx + 1}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Text Overlay Controls — shown when video exists and studio is hidden */}
                                {!showStudio && hasVisual && (visual?.type === 'video' || visual?.metadata?.contentType === 'video/mp4') && (
                                    <div className="px-6 pb-4">
                                        <TextOverlayControls
                                            project={project}
                                            post={currentPost}
                                            platform={platform}
                                            vibeSettings={currentPost.visualSettings ? {categoryId: currentPost.visualSettings.categoryId, vibeId: currentPost.visualSettings.vibeId} : {}}
                                            onOverlayApplied={(newUrl, plat) => {
                                                const updatedVisual = {
                                                    ...visual,
                                                    url: newUrl,
                                                    originalUrl: visual.originalUrl || visual.url,
                                                };
                                                onSetPostVisual(platformPostIndex, updatedVisual, plat);
                                            }}
                                            onPreviewChange={(elements) => {
                                                setTextPreviewElements(prev => ({...prev, [platform]: elements}));
                                            }}
                                            videoDuration={visual?.metadata?.duration || 5}
                                        />
                                    </div>
                                )}

                                {showStudio && (
                                    <div className="bg-warm-gray/30 border-t border-white/5 p-1">
                                        {isVideoTemplate(adjustments?.templates?.[platform]) ? (
                                            <VideoGenerationPanel
                                                project={project}
                                                post={currentPost}
                                                onVisualUpdate={(pid, v, plat, status) => handleVisualUpdateFromAI(pid, v, plat, status)}
                                                adjustments={adjustments}
                                                activePlatform={platform}
                                                workflowType={workflowType}
                                                allPosts={posts}
                                                onGenerateAllForPlatform={handleGenerateAllForPlatform}
                                                isGeneratingBatch={isBatchGenerating}
                                            />
                                        ) : (
                                            <VisualGenerationPanel
                                                project={project}
                                                post={currentPost}
                                                onVisualUpdate={(pid, v, plat, status, sequence) => handleVisualUpdateFromAI(pid, v, plat, status, sequence)}
                                                onSetPostPrompt={(txt) => onSetPostPrompt(platformPostIndex, txt)}
                                                adjustments={adjustments}
                                                activePlatform={platform}
                                                sessionSettings={masterSessionSettings[platform]}
                                                onSettingsChange={(s) => handleSettingsUpdate(s, platform)}
                                                activeSlideIndex={activeCarouselSlide[platform] || 0}
                                                onSlideChange={(idx) => setActiveCarouselSlide(prev => ({ ...prev, [platform]: idx }))}
                                                workflowType={workflowType}
                                                allPosts={posts}
                                                onGenerateAllForPlatform={handleGenerateAllForPlatform}
                                                isGeneratingBatch={isBatchGenerating}
                                            />
                                        )}
                                    </div>
                                )}
                                <PostTextPreview text={platformText} />
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Footer Actions */}
            <div className="pt-4 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4 mt-4">
                <div className="flex items-center gap-6">
                    {/* Buttons handled in panel */}
                </div>

                <button onClick={onVisualsConfirmed} disabled={uploadingState.index !== null || isBatchGenerating} className="w-full md:w-auto btn btn-primary px-12 py-5 shadow-xl transition-all transform active:scale-95 disabled:opacity-50">
                    <FiCheckCircle className="mr-3" size={24} /> {workflowType === 'stream' && posts.length > 1 ? 'Finalize Batch' : 'Finalize Post'}
                </button>
            </div>
        </div>
    );
}

VisualGenerator.propTypes = {
    project: PropTypes.object.isRequired,
    posts: PropTypes.array.isRequired,
    workflowType: PropTypes.string,
    onSetPostVisual: PropTypes.func.isRequired,
    onSetPostPrompt: PropTypes.func.isRequired,
    onVisualsConfirmed: PropTypes.func.isRequired,
    onSettingsChange: PropTypes.func, // FIXED
    loading: PropTypes.bool,
    adjustments: PropTypes.object,
    onBack: PropTypes.func.isRequired,
};

export default VisualGenerator;